import {
  ChevronLeft,
  ChevronRight,
  Code,
  DocumentText,
  ExclamationCircleSolid,
  Eye,
  Trash,
} from '@medusajs/icons';
import {
  Badge,
  Button,
  Checkbox,
  FocusModal,
  IconButton,
  Prompt,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  Text,
  Tooltip,
  TooltipProvider,
  toast,
} from '@pcrm/fe/ui-kit';
import { CURRENCIES, currencyCodes, currencyIsoCode } from '@pcrm/shared/money';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { PaperPreview } from '../../components/paper-preview';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { useHasPermission } from '../auth/use-session';
import {
  BILLING_QUERY_KEY,
  type BillingInvoice,
  type BillingMonthView,
  type BillingRow,
  type BillingRowState,
  billingMonthLabel,
  billingMonthQueryOptions,
  currentBillingMonth,
  deleteInvoices,
  invoiceFileUrl,
  previewInvoiceHtml,
  shiftMonth,
  startInvoiceBatch,
  sumAmounts,
} from './billing.api';

/**
 * Starea implicită a filtrului: **tot ce mai e de facturat**, adică fără emise (#205).
 * `Select` nu acceptă valoare goală; la firmă, aceeași valoare înseamnă „toate firmele".
 */
const ANY = '_toate';

/** Filtrul de stare: cuvintele din ecran, nu codurile serverului. */
export const BILLING_STATE_FILTERS = [
  { value: 'neemise', label: 'Neemise', states: ['ready'] },
  { value: 'blocate', label: 'Nu se pot factura', states: ['blocked'] },
  { value: 'in-lot', label: 'În lot', states: ['queued'] },
  { value: 'emise', label: 'Emise', states: ['issued'] },
] as const satisfies readonly {
  value: string;
  label: string;
  states: readonly BillingRowState[];
}[];

export type BillingStateFilter = (typeof BILLING_STATE_FILTERS)[number]['value'];

/** Câte luni în urmă oferă selectorul; săgețile merg oricât mai departe. */
const MONTHS_BACK = 24;

export interface BillingScreenProps {
  readonly month: string;
  readonly companyId: string | undefined;
  readonly stateFilter: BillingStateFilter | undefined;
  readonly onMonthChange: (month: string) => void;
  readonly onCompanyChange: (companyId: string | undefined) => void;
  readonly onStateFilterChange: (filter: BillingStateFilter | undefined) => void;
}

/**
 * Conta → Facturare automată ([#184](https://github.com/bplugaru/efx-pcrm/issues/184)): **clienții
 * lunii alese care mai sunt de facturat**, fiecare cu starea lui, și un singur buton care pune
 * lotul.
 *
 * ## Lista de lucru, nu arhiva lunii
 *
 * Din [#205](https://github.com/bplugaru/efx-pcrm/issues/205) (fondatorul, 2026-10-13):
 * facturile emise nu apar decât cerute din filtru („Emise"), iar clienții speciali nu apar
 * deloc — pe ei îi scoate serverul, nu ecranul.
 *
 * ## Omul alege, jobul execută
 *
 * Bifele sunt starea ecranului, nu o coloană: la reîncărcare revin la implicitul dat de
 * `auto_invoicing`. „Emite factură (N)" e **singurul drum de emitere** din ecran — o singură
 * factură e un singur rând bifat. Confirmarea arată câte facturi ies, pe ce lună, cu ce dată și
 * ce total pe fiecare firmă: pentru masă, ea e poarta umană.
 *
 * ## Corectura
 *
 * O factură care n-a plecat la ANAF se șterge de aici și se corectează din fișa clientului
 * ([#201](https://github.com/bplugaru/efx-pcrm/issues/201)).
 */
export function BillingScreen(props: BillingScreenProps) {
  const { month, companyId, stateFilter } = props;
  const can = useHasPermission();
  const canIssue = can('invoices.issue');
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery(billingMonthQueryOptions(month, companyId));

  /**
   * Bifele schimbate de mână, peste implicitul dat de `auto_invoicing`.
   *
   * ⚠️ **Nu se salvează.** Un client care trebuie exclus lună de lună se scoate din facturarea
   * automată pe fișa lui, nu ținând minte o bifă. Se golesc la schimbarea lunii sau a firmei:
   * o bifă pusă pe octombrie n-are ce căuta în noiembrie.
   */
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [confirming, setConfirming] = useState(false);
  const [previewing, setPreviewing] = useState<BillingRow | null>(null);
  const [deleting, setDeleting] = useState<BillingInvoice | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: bifele țin de lună și firmă, nu de altceva.
  useEffect(() => {
    setOverrides(new Map());
  }, [month, companyId]);

  const rows = data?.rows ?? [];
  const isChecked = (row: BillingRow) =>
    row.state === 'ready' && (overrides.get(row.customerId) ?? row.autoInvoicing);

  const visible = useMemo(() => filterRows(rows, stateFilter), [rows, stateFilter]);
  const visibleReady = visible.filter((row) => row.state === 'ready');
  const checked = rows.filter(isChecked);
  const visibleChecked = visibleReady.filter(isChecked);
  const allVisibleChecked =
    visibleReady.length > 0 && visibleChecked.length === visibleReady.length;

  const toggle = (row: BillingRow, value: boolean) =>
    setOverrides((current) => new Map(current).set(row.customerId, value));

  /** „Bifează tot" lucrează pe filtrul curent: ce vede omul, nu ce e ascuns. */
  const toggleAll = (value: boolean) =>
    setOverrides((current) => {
      const next = new Map(current);
      for (const row of visibleReady) {
        next.set(row.customerId, value);
      }
      return next;
    });

  const start = useMutation({
    mutationFn: () =>
      startInvoiceBatch({
        month,
        rows: checked.map((row) => ({
          customerId: row.customerId,
          fingerprint: row.fingerprint ?? '',
        })),
      }),
    onSuccess: async (batch) => {
      toast.success(
        `${batch.count} ${batch.count === 1 ? 'factură pusă' : 'facturi puse'} în lot, cu data ${formatDate(batch.issueDate)}.`,
      );
      setConfirming(false);
      setOverrides(new Map());
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
    onError: async (failure: unknown) => {
      setConfirming(false);
      // Lista s-a schimbat de la încărcare: se reîncarcă, iar omul se uită din nou la sume
      // înainte să confirme. Nu se pune nimic în lot pe sume pe care nu le-a văzut.
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
      toast.error(failure instanceof ApiError ? failure.message : 'Lotul nu a putut fi pornit.');
    },
  });

  const remove = useMutation({
    mutationFn: (invoice: BillingInvoice) => deleteInvoices([invoice.id]),
    onSuccess: async () => {
      toast.success(`Factura ${deleting?.series ?? ''} ${deleting?.number ?? ''} a fost ștearsă.`);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
    onError: (failure: unknown) => {
      toast.error(failure instanceof ApiError ? failure.message : 'Factura nu a putut fi ștearsă.');
    },
  });

  return (
    <SectionContainer>
      <SectionHeader title="Facturare automată" />

      <div className="flex flex-wrap items-end gap-3 px-6 py-4">
        <FilterField label="Luna facturată" htmlFor="filtru-luna">
          <MonthPicker month={month} onChange={props.onMonthChange} />
        </FilterField>

        <FilterField label="Firmă" htmlFor="filtru-firma">
          <Select
            value={companyId ?? ANY}
            onValueChange={(value) => props.onCompanyChange(value === ANY ? undefined : value)}
          >
            <Select.Trigger id="filtru-firma" className="w-56">
              <Select.Value placeholder="Toate firmele" />
            </Select.Trigger>
            <Select.Content className="z-50">
              <Select.Item value={ANY}>Toate firmele</Select.Item>
              {/* Lista vine cu luna; fără firmă aleasă are toate firmele grupului. */}
              {(data?.companies ?? []).map((company) => (
                <Select.Item key={company.id} value={company.id}>
                  {company.shortName ?? company.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </FilterField>

        <FilterField label="Stare" htmlFor="filtru-stare">
          <Select
            value={stateFilter ?? ANY}
            onValueChange={(value) =>
              props.onStateFilterChange(value === ANY ? undefined : (value as BillingStateFilter))
            }
          >
            <Select.Trigger id="filtru-stare" className="w-48">
              <Select.Value placeholder="De facturat" />
            </Select.Trigger>
            <Select.Content className="z-50">
              <Select.Item value={ANY}>De facturat</Select.Item>
              {BILLING_STATE_FILTERS.map((filter) => (
                <Select.Item key={filter.value} value={filter.value}>
                  {filter.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </FilterField>
      </div>

      {data ? <Notices view={data} /> : null}

      {isPending ? (
        <div className="flex flex-col gap-3 px-6 py-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : error ? (
        <SectionEmpty tone="error">
          {error instanceof Error ? error.message : 'Lista nu a putut fi citită.'}
        </SectionEmpty>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
            <div className="flex items-center gap-x-3">
              <Checkbox
                id="bifeaza-tot"
                checked={allVisibleChecked}
                disabled={visibleReady.length === 0}
                onCheckedChange={(value) => toggleAll(value === true)}
                aria-label="Bifează tot"
              />
              <label htmlFor="bifeaza-tot" className="txt-compact-small-plus">
                {allVisibleChecked ? 'Debifează tot' : 'Bifează tot'}
              </label>
              {/*
                Numărătoarea e ce face vizibilă o migrare greșită a lui `auto_invoicing`:
                „0 bifați din 300" sau „300 din 300" se vede din prima privire.
              */}
              <Text size="small" className="text-ui-fg-subtle">
                {visibleChecked.length} bifați din {visibleReady.length} neemiși
              </Text>
            </div>

            {canIssue ? (
              <Button
                size="small"
                disabled={checked.length === 0 || start.isPending}
                onClick={() => setConfirming(true)}
              >
                Emite factură ({checked.length})
              </Button>
            ) : null}
          </div>

          {visible.length === 0 ? (
            <SectionEmpty>
              {stateFilter
                ? 'Niciun client pentru filtrele alese.'
                : `Niciun client de facturat pe ${billingMonthLabel(month)}.`}
            </SectionEmpty>
          ) : (
            <BillingTable
              rows={visible}
              view={data}
              canIssue={canIssue}
              isChecked={isChecked}
              onToggle={toggle}
              onPreview={setPreviewing}
              onDelete={setDeleting}
            />
          )}
        </>
      )}

      {data ? (
        <ConfirmBatch
          open={confirming}
          view={data}
          rows={checked}
          isPending={start.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => start.mutate()}
        />
      ) : null}

      <PreviewModal month={month} row={previewing} onClose={() => setPreviewing(null)} />

      <Prompt
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        variant="danger"
      >
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              Ștergi factura {deleting?.series} {deleting?.number}?
            </Prompt.Title>
            <Prompt.Description>
              Rândul revine la „Neemisă". Dacă e ultima factură din serie, numărul ei se refolosește
              la următoarea; dacă după ea mai sunt facturi, numărul rămâne gol.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action
              onClick={(event) => {
                event.preventDefault();
                if (deleting) {
                  remove.mutate(deleting);
                }
              }}
            >
              Șterge
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </SectionContainer>
  );
}

function filterRows(
  rows: readonly BillingRow[],
  filter: BillingStateFilter | undefined,
): BillingRow[] {
  if (filter === undefined) {
    // ⚠️ Fără filtru nu înseamnă „tot": emisele se văd doar cerute (#205).
    return rows.filter((row) => row.state !== 'issued');
  }

  const states: readonly BillingRowState[] =
    BILLING_STATE_FILTERS.find((item) => item.value === filter)?.states ?? [];

  return rows.filter((row) => states.includes(row.state));
}

/**
 * Selectorul de lună: luna aleasă, cu săgeți pentru luna dinainte și cea de după.
 *
 * Valoarea implicită e luna curentă, la București — doar un punct de plecare. Luna stă în
 * adresa ecranului, ca un link trimis cuiva să deschidă aceeași listă.
 */
function MonthPicker({ month, onChange }: { month: string; onChange: (month: string) => void }) {
  const current = currentBillingMonth();
  const options = useMemo(() => {
    const list = Array.from({ length: MONTHS_BACK + 2 }, (_, index) =>
      shiftMonth(current, 1 - index),
    );

    return list.includes(month) ? list : [month, ...list].sort().reverse();
  }, [current, month]);

  return (
    <div className="flex items-center gap-x-1">
      <IconButton
        size="small"
        variant="transparent"
        aria-label="Luna dinainte"
        onClick={() => onChange(shiftMonth(month, -1))}
      >
        <ChevronLeft />
      </IconButton>
      <Select value={month} onValueChange={onChange}>
        <Select.Trigger id="filtru-luna" className="w-60">
          <Select.Value />
        </Select.Trigger>
        <Select.Content className="z-50">
          {options.map((option) => (
            <Select.Item key={option} value={option}>
              {billingMonthLabel(option)}
              {option === current ? ' · luna curentă' : ''}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      <IconButton
        size="small"
        variant="transparent"
        aria-label="Luna de după"
        onClick={() => onChange(shiftMonth(month, 1))}
      >
        <ChevronRight />
      </IconButton>
    </div>
  );
}

/**
 * Ce trebuie văzut înainte de bife: cota de TVA, cursul, firmele fără serie, loturile în lucru.
 * Nu se ascunde nimic tăcut — dar nici ce nu ajută pe nimeni (#201: „Ultimul lot…" a ieșit).
 */
function Notices({ view }: { view: BillingMonthView }) {
  const withoutSeries = view.companies.filter((company) => company.series === null);
  const running = view.batches.filter((batch) => batch.queued + batch.processing > 0);

  const notes: {
    key: string;
    tone: 'red' | 'orange' | 'blue' | 'grey';
    text: React.ReactNode;
  }[] = [];

  if (view.vatRate.problem !== null) {
    notes.push({ key: 'cota', tone: 'red', text: `Cota de TVA: ${view.vatRate.problem}` });
  }

  // Cursul se arată doar când lista are prețuri în moneda lui, încă nefacturate: într-o lună doar
  // în lei, nici cursul, nici avertismentul de 13:00 n-au ce spune.
  const pendingCurrencies = new Set(
    view.rows
      .filter((row) => row.state !== 'issued')
      .flatMap((row) => row.subscriptions.map((subscription) => subscription.currency)),
  );

  for (const rate of view.exchangeRates) {
    const code = currencyCodes.find((item) => currencyIsoCode(item) === rate.currency);

    if (code === undefined || !pendingCurrencies.has(code)) {
      continue;
    }

    notes.push(
      rate.problem !== null
        ? { key: `curs-${rate.currency}`, tone: 'red', text: rate.problem }
        : {
            key: `curs-${rate.currency}`,
            tone: 'grey',
            text: `Curs ${rate.currency}: 1 ${rate.currency} = ${formatRate(rate.value ?? '')} lei, din ${formatDate(rate.date ?? '')}. Prețurile în ${rate.currency} se convertesc cu el, iar factura îl păstrează.`,
          },
    );
  }

  // #214: singurul semnal despre vârsta cursului. Nu blochează; operatorul hotărăște dacă așteaptă.
  if (view.exchangeRateUpdateAt !== null && notes.some((note) => note.key.startsWith('curs-'))) {
    notes.push({
      key: 'curs-actualizare',
      tone: 'orange',
      text: rateUpdateNotice(view.exchangeRateUpdateAt),
    });
  }

  for (const company of withoutSeries) {
    notes.push({
      key: `serie-${company.id}`,
      tone: 'orange',
      text: (
        <>
          <Link
            to="/setari/firme/$companyId"
            params={{ companyId: company.id }}
            search={{ tab: 'financiar' }}
            className="text-ui-fg-interactive hover:underline"
          >
            {company.shortName ?? company.name}
          </Link>{' '}
          n-are serie de facturi: rândurile ei nu se pot factura.
        </>
      ),
    });
  }

  for (const batch of running) {
    notes.push({
      key: `lot-${batch.id}`,
      tone: 'blue',
      text: `Lot în lucru, cu data ${formatDate(batch.issueDate)}: ${batch.issued} emise, ${batch.skipped} sărite, ${batch.queued + batch.processing} în așteptare.`,
    });
  }

  if (notes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-2 px-6 py-3">
      {notes.map((note) => (
        <div key={note.key} className="flex items-start gap-x-2">
          <StatusBadge color={note.tone}>{TONE_LABEL[note.tone]}</StatusBadge>
          <Text size="small">{note.text}</Text>
        </div>
      ))}
    </div>
  );
}

const TONE_LABEL = { red: 'Blocat', orange: 'Atenție', blue: 'Info', grey: 'Info' } as const;

/** Avertismentul de dinainte de 13:00, același în listă și în fișa clientului (#214). */
export function rateUpdateNotice(time: string): string {
  return `Cursul BNR de azi se actualizează la ${time}. Până atunci, facturile în valută se emit cu cursul de mai sus; poți aștepta sau poți emite acum.`;
}

/** Cursul cum l-a scris operatorul, cu virgulă: `4.9750` → `4,9750`. Nu trece prin `number`. */
function formatRate(rate: string): string {
  return rate.replace('.', ',');
}

/**
 * Abonamentul în listă: numele, iar la cel în valută și prețul în moneda lui — totalul din
 * coloana de alături e deja în lei, convertit.
 */
function subscriptionLabel(subscription: BillingRow['subscriptions'][number]): string {
  return subscription.currency === CURRENCIES.ron || subscription.cost === null
    ? subscription.name
    : `${subscription.name} (${formatMoney(subscription.cost, subscription.currency)})`;
}

function BillingTable({
  rows,
  view,
  canIssue,
  isChecked,
  onToggle,
  onPreview,
  onDelete,
}: {
  rows: readonly BillingRow[];
  view: BillingMonthView;
  canIssue: boolean;
  isChecked: (row: BillingRow) => boolean;
  onToggle: (row: BillingRow, value: boolean) => void;
  onPreview: (row: BillingRow) => void;
  onDelete: (invoice: BillingInvoice) => void;
}) {
  const companies = new Map(view.companies.map((company) => [company.id, company]));

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell className="w-[44px]" />
          <Table.HeaderCell>Client</Table.HeaderCell>
          <Table.HeaderCell>Firmă</Table.HeaderCell>
          <Table.HeaderCell>Abonamente</Table.HeaderCell>
          <Table.HeaderCell className="w-[120px] text-right">Total</Table.HeaderCell>
          <Table.HeaderCell>Stare</Table.HeaderCell>
          <Table.HeaderCell className="w-[150px]" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row) => {
          const company = companies.get(row.companyId);

          return (
            <Table.Row key={row.customerId} className="align-top">
              <Table.Cell>
                {/* Doar rândul care se poate factura are bifă: ce e emis, în lot sau blocat, nu. */}
                {row.state === 'ready' ? (
                  <Checkbox
                    checked={isChecked(row)}
                    disabled={!canIssue}
                    onCheckedChange={(value) => onToggle(row, value === true)}
                    aria-label={`Bifează ${row.nickname}`}
                  />
                ) : null}
              </Table.Cell>
              <Table.Cell>
                <div className="flex flex-col gap-y-1 py-2">
                  <span className="flex flex-wrap items-center gap-x-2">
                    <span className="font-medium">{row.nickname}</span>
                    {row.markers.length > 0 ? <RowMarkers markers={row.markers} /> : null}
                    {row.autoInvoicing ? null : (
                      <Badge size="2xsmall" color="grey">
                        Fără facturare automată
                      </Badge>
                    )}
                  </span>
                  <Text size="small" className="text-ui-fg-subtle">
                    {row.name}
                  </Text>
                </div>
              </Table.Cell>
              <Table.Cell className="text-ui-fg-subtle">
                {company ? (company.shortName ?? company.name) : '-'}
                {/* Seria apare mereu lângă firmă: aceeași serie poate fi la două firme (#188). */}
                {row.invoice ? null : company?.series ? (
                  <Text size="xsmall" className="text-ui-fg-muted">
                    seria {company.series.code}
                  </Text>
                ) : null}
              </Table.Cell>
              <Table.Cell className="text-ui-fg-subtle">
                {row.state === 'issued'
                  ? '-'
                  : row.subscriptions.map(subscriptionLabel).join(', ') || '-'}
              </Table.Cell>
              <Table.Cell className="text-right">
                {row.totals ? formatMoney(row.totals.payable) : '-'}
              </Table.Cell>
              <Table.Cell>
                <RowState row={row} />
              </Table.Cell>
              <Table.Cell>
                <RowActions
                  row={row}
                  canIssue={canIssue}
                  onPreview={() => onPreview(row)}
                  onDelete={onDelete}
                />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}

/**
 * Semnele rândului (abonament gratuit, expirat, fără preț), strânse sub un icon galben: textul
 * apare la trecerea mouse-ului sau la focus (#205). Rândul se poate factura oricum — de asta nu
 * ocupă loc în listă, spre deosebire de motivele unui rând blocat.
 */
function RowMarkers({ markers }: { markers: BillingRow['markers'] }) {
  return (
    <TooltipProvider>
      <Tooltip
        // La dreapta: iconul stă lângă marginea listei, iar deasupra lui textul intră sub meniu.
        side="right"
        maxWidth={360}
        content={
          <span className="flex flex-col gap-y-1">
            {markers.map((marker) => (
              <span key={marker.code + marker.message}>{marker.message}</span>
            ))}
          </span>
        }
      >
        <button
          type="button"
          className="inline-flex cursor-default text-yellow-500"
          aria-label={`Atenție: ${markers.map((marker) => marker.message).join(' ')}`}
        >
          <ExclamationCircleSolid />
        </button>
      </Tooltip>
    </TooltipProvider>
  );
}

function RowState({ row }: { row: BillingRow }) {
  if (row.state === 'issued' && row.invoice) {
    const { invoice } = row;

    return (
      <div className="flex flex-col gap-y-1 py-2">
        <span className="flex items-center gap-x-2">
          <StatusBadge color="green">Emisă</StatusBadge>
        </span>
        <Text size="small">
          {invoice.series} {invoice.number} · {formatDate(invoice.issueDate)}
        </Text>
        {invoice.files.error ? (
          <Text size="xsmall" className="text-ui-fg-error">
            Fișierele se refac: {invoice.files.error}
          </Text>
        ) : null}
      </div>
    );
  }

  if (row.state === 'queued') {
    return (
      <StatusBadge color="blue">
        {row.batchStatus === 'processing' ? 'În lucru' : 'În lot'}
      </StatusBadge>
    );
  }

  if (row.state === 'blocked') {
    return (
      <div className="flex flex-col gap-y-1 py-2">
        <StatusBadge color="red">Nu se poate factura</StatusBadge>
        {row.reasons.map((reason) => (
          <Text key={reason.code + reason.message} size="xsmall" className="text-ui-fg-subtle">
            {reason.message}
          </Text>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-1 py-2">
      <StatusBadge color="grey">Neemisă</StatusBadge>
      {row.lastSkip ? (
        <Text size="xsmall" className="text-ui-fg-subtle">
          Sărit la ultimul lot: {row.lastSkip.reason}
        </Text>
      ) : null}
    </div>
  );
}

function RowActions({
  row,
  canIssue,
  onPreview,
  onDelete,
}: {
  row: BillingRow;
  canIssue: boolean;
  onPreview: () => void;
  onDelete: (invoice: BillingInvoice) => void;
}) {
  if (row.state === 'ready') {
    return (
      <Button size="small" variant="transparent" onClick={onPreview}>
        <Eye />
        Previzualizează
      </Button>
    );
  }

  if (row.state !== 'issued' || row.invoice === null) {
    return null;
  }

  const { invoice } = row;

  return (
    <div className="flex items-center gap-x-1">
      <FileLink href={invoiceFileUrl(invoice.id, 'pdf')} ready={invoice.files.pdf} label="PDF">
        <DocumentText />
      </FileLink>
      <FileLink href={invoiceFileUrl(invoice.id, 'xml')} ready={invoice.files.xml} label="XML">
        <Code />
      </FileLink>
      {canIssue && invoice.editable ? (
        <IconButton
          size="small"
          variant="transparent"
          aria-label={`Șterge factura ${invoice.series} ${invoice.number}`}
          onClick={() => onDelete(invoice)}
        >
          <Trash />
        </IconButton>
      ) : null}
    </div>
  );
}

/**
 * Fișierul se face după emitere; până atunci, butonul spune că vine, nu duce la un 409.
 * Îl folosește și tabul Financiar din fișa clientului (#186).
 */
export function FileLink({
  href,
  ready,
  label,
  children,
}: {
  href: string;
  ready: boolean;
  label: string;
  children: React.ReactNode;
}) {
  if (!ready) {
    return (
      <Button
        size="small"
        variant="transparent"
        disabled
        title="Fișierul se face imediat după emitere."
      >
        {children}
        {label}
      </Button>
    );
  }

  return (
    <Button size="small" variant="transparent" asChild>
      <a href={href} target="_blank" rel="noreferrer">
        {children}
        {label}
      </a>
    </Button>
  );
}

/**
 * Confirmarea dinaintea lotului: **câte facturi, ce lună, ce dată, ce total pe fiecare firmă**.
 *
 * ⚠️ Pentru masă, asta e poarta umană din #170. După trecerea în producție, o factură reală
 * emisă greșit nu se poate desface până la storno (G8) — un lot de sute ar fi o lună de
 * stornări. De asta totalurile pe firmă nu sunt un moft.
 */
function ConfirmBatch({
  open,
  view,
  rows,
  isPending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  view: BillingMonthView;
  rows: readonly BillingRow[];
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const perCompany = view.companies
    .map((company) => {
      const own = rows.filter((row) => row.companyId === company.id);

      return {
        company,
        count: own.length,
        total: sumAmounts(own.map((row) => row.totals?.payable ?? '0.00')),
      };
    })
    .filter((entry) => entry.count > 0);

  return (
    // `confirmation`, nu `danger`: confirmarea unui lot nu distruge nimic — doar consumă numere.
    <Prompt variant="confirmation" open={open} onOpenChange={(next) => !next && onCancel()}>
      <Prompt.Content>
        <Prompt.Header>
          <Prompt.Title>
            {rows.length} {rows.length === 1 ? 'factură' : 'facturi'} pentru {view.monthLabel}, data{' '}
            {formatDate(view.issueDate)}
          </Prompt.Title>
          <Prompt.Description asChild>
            <div className="flex flex-col gap-y-2">
              <ul className="flex flex-col gap-y-1">
                {perCompany.map(({ company, count, total }) => (
                  <li key={company.id}>
                    <Text size="small">
                      {company.shortName ?? company.name}
                      {company.series ? ` (seria ${company.series.code})` : ''} — {count}{' '}
                      {count === 1 ? 'factură' : 'facturi'}, total {formatMoney(total)}
                    </Text>
                  </li>
                ))}
              </ul>
              <Text size="small" className="text-ui-fg-subtle">
                Facturile se emit una câte una, cu data de azi. Un rând al cărui preț, adresă sau
                cotă se schimbă până ajunge jobul la el se sare, cu motivul scris.
              </Text>
            </div>
          </Prompt.Description>
        </Prompt.Header>
        <Prompt.Footer>
          <Prompt.Cancel disabled={isPending}>Renunță</Prompt.Cancel>
          <Prompt.Action
            disabled={isPending}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            Confirmă
          </Prompt.Action>
        </Prompt.Footer>
      </Prompt.Content>
    </Prompt>
  );
}

/**
 * „Previzualizează": factura cu sumele reale și modelul firmei, fără număr și fără să salveze
 * nimic. Același HTML pe care îl primește motorul de PDF la emitere.
 */
function PreviewModal({
  month,
  row,
  onClose,
}: {
  month: string;
  row: BillingRow | null;
  onClose: () => void;
}) {
  const preview = useQuery({
    queryKey: [...BILLING_QUERY_KEY, 'preview', month, row?.customerId ?? null] as const,
    queryFn: () => previewInvoiceHtml(month, row?.customerId ?? ''),
    enabled: row !== null,
    // O previzualizare veche e una mincinoasă: se cere din nou la fiecare deschidere.
    gcTime: 0,
  });

  return (
    <FocusModal open={row !== null} onOpenChange={(next) => !next && onClose()}>
      <FocusModal.Content>
        <FocusModal.Header>
          <div className="flex w-full items-center justify-between gap-x-2">
            <FocusModal.Title asChild>
              <Text size="large" weight="plus">
                Previzualizare — {row?.nickname} · {billingMonthLabel(month)}
              </Text>
            </FocusModal.Title>
            <FocusModal.Description className="sr-only">
              Factura, fără număr și fără să fie salvată.
            </FocusModal.Description>
          </div>
        </FocusModal.Header>
        <FocusModal.Body className="overflow-auto bg-ui-bg-subtle p-6">
          {preview.error ? (
            <SectionEmpty tone="error">
              {preview.error instanceof Error
                ? preview.error.message
                : 'Previzualizarea nu a putut fi făcută.'}
            </SectionEmpty>
          ) : (
            <PaperPreview html={preview.data ?? null} />
          )}
        </FocusModal.Body>
      </FocusModal.Content>
    </FocusModal>
  );
}

function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-y-1">
      <label htmlFor={htmlFor} className="text-ui-fg-subtle txt-compact-small-plus">
        {label}
      </label>
      {children}
    </div>
  );
}
